import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Layout } from "../components/layout/Layout";
import apiService from "../services/api";
import { formatClinicTime, formatClinicRelative } from "../utils/israelTime";
import { useAISupportSSE, AISupportEvent } from "../hooks/useAISupportSSE";
import {
  User,
  AISupportSessionSummary,
  AISupportSessionDetail,
  AISupportPendingAction,
  AISupportStoredMessage,
  AISupportKBEntry,
  AI_SUPPORT_KB_AREAS,
  AISupportAction,
  AISupportDefect,
  AISupportDerived,
  AISupportKBRevision,
} from "../types/api";

const TEAL = "#3A7380";
const AMBER = "#8A5A12";
const ROSE = "#8E2F41";

interface Props {
  user: User;
  onLogout: () => void;
  onNavigate?: (
    screen: "calendar" | "patients" | "tasks" | "internal-tasks" | "patient-page" | "patient" | "users" | "connections" | "billing" | "whatsapp" | "adhoc-meetings" | "ai-support",
    patientId?: string | number,
    meetingId?: string | number
  ) => void;
  taskCount?: number;
  whatsappUnreadCount?: number;
}

const STATUS_STYLE: Record<string, { label: string; color: string; bg: string }> = {
  awaiting_approval: { label: "Needs approval", color: AMBER, bg: "#F6EBD6" },
  escalated: { label: "Escalated", color: ROSE, bg: "#F7E2E4" },
  // "Open" is the state of the conversation; "Live" is whether anyone is
  // looking at it. An unclosed conversation nobody has open is Open, not Live.
  active: { label: "Open", color: TEAL, bg: "#DCECEA" },
  working: { label: "Working", color: "#2F5FA8", bg: "#DDE7F7" },
  closed: { label: "Closed", color: "#6b7b7a", bg: "#eef1f1" },
};
const LIVE_STYLE = { label: "Live", color: "#1F6F3F", bg: "#DFF1E4" };

const CLASS_LABEL: Record<string, string> = {
  knowledge: "Knowledge",
  defect: "Defect",
  intervention: "Intervention",
  unknown: "Unclear",
};
/** Teal reads as resolved, rose as broken, amber as needs-a-hand. */
const CLASS_COLOR: Record<string, string> = {
  knowledge: TEAL,
  defect: ROSE,
  intervention: AMBER,
  unknown: "#6b7b7a",
};
const CAUSE_LABEL = (code: string) => code.replace(/_/g, " ");

const OUTCOME_LABEL: Record<string, string> = {
  fixed: "Solved",
  helped: "Helped",
  didnt_help: "Didn't help",
  need_human: "Needed a human",
  closed_by_support: "Closed by support",
};

/** One filter row, used by both tabs so they look and behave the same. */
const FilterChips: React.FC<{
  options: Array<{ value: string; label: string; count?: number }>;
  value: string;
  onChange: (value: string) => void;
}> = ({ options, value, onChange }) => (
  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
    {options.map((option) => {
      const active = option.value === value;
      return (
        <button
          data-ai-id="console-filter-chip"
          key={option.value || "all"}
          type="button"
          onClick={() => onChange(option.value)}
          style={{
            border: `1px solid ${active ? TEAL : "#dfe7e6"}`,
            background: active ? "#DCECEA" : "#fff",
            color: active ? TEAL : "#6b7b7a",
            borderRadius: 12, padding: "3px 11px", fontSize: 11.5,
            cursor: "pointer", fontWeight: active ? 600 : 400,
          }}
        >
          {option.label}
          {option.count !== undefined ? ` ${option.count}` : ""}
        </button>
      );
    })}
  </div>
);

const Pill: React.FC<{ text: string; color: string; bg: string; title?: string }> = ({
  text, color, bg, title,
}) => (
  <span
    title={title}
    style={{
      display: "inline-block", fontSize: 10, letterSpacing: "0.05em",
      textTransform: "uppercase", padding: "1px 6px", borderRadius: 3,
      border: `1px solid ${color}`, color, background: bg, whiteSpace: "nowrap",
    }}
  >
    {text}
  </span>
);

/**
 * Flatten stored Anthropic blocks into something a human can scan.
 *
 * `conversationOnly` drops the reasoning and the tool traffic, leaving what the
 * therapist actually saw. Reading a conversation the way they experienced it is
 * a different job from debugging how the answer was reached, and the working is
 * usually far longer than the answer.
 */
const renderBlocks = (
  message: AISupportStoredMessage,
  conversationOnly = false
): React.ReactNode => {
  if (message.text) return message.text;
  const blocks = message.blocks || [];
  const parts: string[] = [];
  blocks.forEach((block: any) => {
    if (block?.type === "text" && block.text) parts.push(block.text);
    else if (conversationOnly) return;
    else if (block?.type === "thinking" && block.thinking) parts.push(`💭 ${block.thinking}`);
    else if (block?.type === "tool_use") parts.push(`🔧 ${block.name}(${JSON.stringify(block.input ?? {})})`);
    else if (block?.type === "tool_result") {
      const body = typeof block.content === "string" ? block.content : JSON.stringify(block.content);
      parts.push(`${block.is_error ? "⚠️" : "↩︎"} ${String(body).slice(0, 600)}`);
    }
  });
  if (parts.length) return parts.join("\n\n");
  // A turn that was only reasoning and tool calls has nothing to show here;
  // saying so beats an empty box that looks like a rendering fault.
  return conversationOnly ? null : "(no content)";
};

/** Whether a message is part of the conversation rather than the working. */
const isConversation = (m: AISupportStoredMessage): boolean => {
  if (m.role === "user" || m.role === "assistant" || m.role === "support_reply") {
    // A "user" message is also how tool results are stored, so a turn with no
    // readable text is machinery however it is labelled.
    return Boolean(m.text) || (m.blocks || []).some(
      (b: any) => b?.type === "text" && b.text
    );
  }
  return false;
};

/** Honours prefers-reduced-motion: the dot holds steady instead of pulsing. */
const PULSE_KEYFRAMES = `
@keyframes aiSupportPulse { 0%,100% { opacity: 1 } 50% { opacity: 0.25 } }
@media (prefers-reduced-motion: reduce) {
  [style*="aiSupportPulse"] { animation: none !important }
}`;

const AISupportConsoleScreen: React.FC<Props> = ({
  user, onLogout, onNavigate, taskCount, whatsappUnreadCount,
}) => {
  const [sessions, setSessions] = useState<AISupportSessionSummary[]>([]);
  const [stats, setStats] = useState<{ total_rated: number; resolution_rate: number | null } | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<AISupportSessionDetail | null>(null);
  const [guidance, setGuidance] = useState("");
  // Separate from busyAction, which holds the id of an approval being decided.
  const [isClosing, setIsClosing] = useState(false);
  // Read the conversation as the therapist experienced it, without the
  // reasoning and tool traffic. Off by default: a supporter opening a session
  // is usually there to work out what went wrong.
  const [conversationOnly, setConversationOnly] = useState(false);
  const [isClassifying, setIsClassifying] = useState(false);
  // On while the assistant is being built: support admins' own sessions are most
  // of the traffic, and hiding them leaves the console looking empty.
  const [includeTest, setIncludeTest] = useState(true);
  // Candidates to attach the open session to, so a repeat becomes a count
  // rather than a second record for the same fault.
  const [defectOptions, setDefectOptions] = useState<AISupportDefect[]>([]);
  const [defects, setDefects] = useState<AISupportDefect[]>([]);
  // The register lists every defect whatever its state; this narrows what is
  // shown without changing what was loaded, so the chip counts stay honest.
  const [defectFilter, setDefectFilter] = useState<string>("");
  // Renaming happens in place. One at a time: a defect's title is what everyone
  // else searches for, so it should be a deliberate edit rather than a field
  // sitting live under the cursor.
  const [renamingDefect, setRenamingDefect] = useState<number | null>(null);
  const [defectTitleDraft, setDefectTitleDraft] = useState<string>("");
  const [newDefectTitle, setNewDefectTitle] = useState<string>("");
  // The title being typed for a fault opened from the conversation panel. This
  // was a window.prompt: a browser that has been told to block further dialogs
  // returns null from it silently, so the button did nothing and the supporter
  // was left thinking the defect had been created.
  const [newFaultTitle, setNewFaultTitle] = useState<string | null>(null);
  const [ruleAreas, setRuleAreas] = useState<string[]>([]);
  const [ruleArea, setRuleArea] = useState<string>("");
  const [derived, setDerived] = useState<AISupportDerived | null>(null);
  const [rulesError, setRulesError] = useState<string | null>(null);
  const [causeCodes, setCauseCodes] = useState<Record<string, string[]>>({});
  // How the load actually splits — the count that did not exist before, and the
  // whole point of recording a class.
  const [classStats, setClassStats] = useState<{
    decided: Record<string, number>;
    awaiting_decision: Record<string, number>;
    causes: Record<string, number>;
  } | null>(null);
  const [closeNote, setCloseNote] = useState("");
  const [reply, setReply] = useState("");
  const [busyAction, setBusyAction] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The assistant works in a background task, so the console has to show that
  // something is happening between sending guidance and the answer arriving.
  const [isProcessing, setIsProcessing] = useState(false);
  const [activity, setActivity] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [tab, setTab] = useState<"sessions" | "defects" | "rules" | "kb">("sessions");
  const [kb, setKb] = useState<AISupportKBEntry[]>([]);
  const [editing, setEditing] = useState<AISupportKBEntry | null>(null);
  // Knowledge that came from nobody's session: a rule someone knows, written
  // straight in. Distillation only ever produces entries for problems that have
  // already happened, so without this there is no way to get ahead of one.
  const [authoring, setAuthoring] = useState<Partial<AISupportKBEntry> | null>(null);
  // Changes distillation proposes to entries that already exist. Reviewed like
  // a draft: the entry they touch was published by a person.
  const [revisions, setRevisions] = useState<AISupportKBRevision[]>([]);
  const [revisionEntries, setRevisionEntries] = useState<Record<string, AISupportKBEntry | null>>({});
  const [savingKb, setSavingKb] = useState(false);
  const [actions, setActions] = useState<AISupportAction[]>([]);
  const [sessionFilter, setSessionFilter] = useState<string>("");
  // How many conversations each chip would show, counted server-side across
  // every session. It cannot be derived here: the list is fetched already
  // filtered, so counting it gives every other chip the wrong number.
  const [filterCounts, setFilterCounts] = useState<Record<string, number>>({});
  // Whether the server sent any. Distinguishes "every filter is empty" from
  // "this backend does not report counts", which look identical otherwise.
  const hasFilterCounts = Object.keys(filterCounts).length > 0;
  const [kbFilter, setKbFilter] = useState<string>("");
  const [kbSearch, setKbSearch] = useState<string>("");
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  // Auto-scroll only while the supporter is already reading the newest message.
  // The 20s poll replaces `detail` even when nothing changed, so scrolling on
  // every update would yank them back down mid-read.
  const pinnedToBottomRef = useRef(true);
  const scrolledSessionRef = useRef<number | null>(null);
  const messageCountRef = useRef(0);
  // Read by the 20s poll so it always sees the current selection without the
  // timer being torn down and recreated on every click.
  const selectedIdRef = useRef<number | null>(null);

  const token = () => localStorage.getItem("token") || "";

  useEffect(() => {
    apiService
      .getAISupportActions(token())
      .then((res) => setActions(res.actions))
      .catch((e) => console.error("[AISupportConsole] actions failed", e));
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const res = await apiService.getAISupportSessions(
        token(),
        {
          // The chip counts are over every session, so the page has to be big
          // enough to hold what they promise. 200 is the server's cap; past that
          // the list needs paging rather than a bigger number.
          limit: 200,
          include_test: includeTest,
          // Every chip, including "live" and "needs classifying", is applied by
          // the server. Filtering those two here instead meant they were counted
          // across all sessions but matched against only the page the console
          // was holding, so a chip could show a number and then open an empty
          // list -- which is what it did as soon as there were more sessions
          // than one page.
          ...(sessionFilter ? { status: sessionFilter } : {}),
        }
      );
      setSessions(res.sessions);
      setStats(res.stats);
      if (res.filter_counts) setFilterCounts(res.filter_counts);
      apiService
        .getAISupportClasses(token(), includeTest)
        .then((r) => setClassStats(r.stats || null))
        .catch(() => {});
      setSelectedId((current) => current ?? res.sessions[0]?.id ?? null);
    } catch (e: any) {
      console.error("[AISupportConsole] list failed", e);
      setError(e?.response?.data?.detail || "Could not load sessions.");
    }
  }, [sessionFilter, includeTest]);

  const loadDetail = useCallback(async (sessionId: number) => {
    try {
      setDetail(await apiService.getAISupportSession(token(), sessionId));
    } catch (e: any) {
      console.error("[AISupportConsole] detail failed", e);
      setError(e?.response?.data?.detail || "Could not load the conversation.");
    }
  }, []);

  useEffect(() => { void loadSessions(); }, [loadSessions]);

  useEffect(() => {
    apiService
      .getAISupportClasses(token(), includeTest)
      .then((r) => {
        setCauseCodes(r.cause_codes || {});
        setClassStats(r.stats || null);
      })
      .catch(() => setCauseCodes({}));
  }, [includeTest]);

  // Poll so new conversations and status changes appear without a reload. The
  // open conversation updates over SSE as well, but the poll keeps it current
  // if the stream drops.
  useEffect(() => {
    const timer = setInterval(() => {
      void loadSessions();
      if (selectedIdRef.current) void loadDetail(selectedIdRef.current);
    }, 20000);
    return () => clearInterval(timer);
  }, [loadSessions, loadDetail]);

  useEffect(() => {
    selectedIdRef.current = selectedId;
    setIsProcessing(false);
    setActivity(null);
    setError(null);
    if (selectedId) void loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  useEffect(() => {
    const el = transcriptRef.current;
    if (!el) return;
    const count = detail?.messages?.length ?? 0;
    const sessionId = detail?.session?.id ?? null;
    const sessionChanged = scrolledSessionRef.current !== sessionId;
    const grew = count > messageCountRef.current;
    scrolledSessionRef.current = sessionId;
    messageCountRef.current = count;
    if (sessionChanged) pinnedToBottomRef.current = true;
    if (sessionChanged || (grew && pinnedToBottomRef.current)) {
      el.scrollTop = el.scrollHeight;
    }
  }, [detail]);

  // Watching over the therapist's shoulder: the supporter subscribes to the same
  // channel they do, and sees the support-only frames too.
  const handleEvent = useCallback((event: AISupportEvent) => {
    switch (event.type) {
      case "token_delta":
        setIsProcessing(true);
        setActivity(null);
        return;
      case "tool_start":
        setIsProcessing(true);
        setActivity(event.tool || null);
        return;
      case "tool_end":
        setActivity(null);
        return;
      case "error":
        setIsProcessing(false);
        setActivity(null);
        setError(event.message || "The assistant hit an error on this turn.");
        return;
      case "assistant_done":
      case "approval_required":
        setIsProcessing(false);
        setActivity(null);
        break;
      case "approval_decided":
        setIsProcessing(true);
        break;
      default:
        break;
    }
    if (["assistant_done", "approval_required", "approval_decided", "escalated",
         "guidance", "support_reply", "action_ran", "status"].includes(event.type)) {
      if (selectedId) void loadDetail(selectedId);
      void loadSessions();
    }
  }, [selectedId, loadDetail, loadSessions]);

  // "console": watching here must never mark the conversation live.
  useAISupportSSE(selectedId, selectedId !== null, handleEvent, "console");

  const decide = async (action: AISupportPendingAction, decision: "approve" | "deny") => {
    setBusyAction(action.id);
    setError(null);
    try {
      await apiService.decideAISupportAction(token(), action.id, decision);
      if (decision === "approve") setIsProcessing(true);
      if (selectedId) await loadDetail(selectedId);
      await loadSessions();
    } catch (e: any) {
      console.error("[AISupportConsole] decision failed", e);
      setError(e?.response?.data?.detail || "Could not record that decision.");
    } finally {
      setBusyAction(null);
    }
  };

  const sendGuidance = async () => {
    const text = guidance.trim();
    if (!text || !selectedId) return;
    setGuidance("");
    setError(null);
    setIsProcessing(true);
    try {
      await apiService.sendAISupportGuidance(token(), selectedId, text);
      await loadDetail(selectedId);
    } catch (e: any) {
      console.error("[AISupportConsole] guidance failed", e);
      setError(e?.response?.data?.detail || "Could not send that guidance.");
      setIsProcessing(false);
    }
  };

  const loadDefects = useCallback(async () => {
    try {
      const res = await apiService.getAISupportDefects(token());
      setDefects(res.defects || []);
    } catch (e: any) {
      // Swallowing this showed an empty register, which reads as "there are no
      // defects" rather than "the register could not be read" -- the two need
      // very different responses from whoever is looking at it.
      console.error("[AISupportConsole] defects load failed", e);
      setError(e?.response?.data?.detail || "Could not load the defect register.");
      setDefects([]);
    }
  }, []);

  useEffect(() => { void loadDefects(); }, [loadDefects]);

  useEffect(() => {
    if (tab !== "rules") return;
    apiService
      .getAISupportDerived(token())
      .then((r) => {
        setRuleAreas(r.areas || []);
        setRuleArea((current) => current || (r.areas || [])[0] || "");
      })
      .catch(() => setRuleAreas([]));
  }, [tab]);

  useEffect(() => {
    if (tab !== "rules" || !ruleArea) return;
    setRulesError(null);
    apiService
      .getAISupportDerived(token(), ruleArea)
      .then((r) => setDerived(r.derived || null))
      .catch((e) => {
        setDerived(null);
        // A generator that cannot read its source is worth seeing, not hiding:
        // it means the assistant is answering without those rules.
        setRulesError(e?.response?.data?.detail || "Could not derive that area.");
      });
  }, [tab, ruleArea]);

  // The symptom and cause come from the caller: this is defined above the
  // memo that resolves the selected session.
  const attachToDefect = async (
    defectId: number | null,
    ctx: { title?: string; symptom?: string; causeCode?: string } = {}
  ) => {
    if (!selectedId) return;
    setError(null);
    try {
      await apiService.linkAISupportDefect(token(), {
        session_id: selectedId,
        defect_id: defectId,
        title: ctx.title,
        symptom: ctx.symptom,
        cause_code: ctx.causeCode,
      });
      await Promise.all([loadDetail(selectedId), loadSessions(), loadDefects()]);
    } catch (e: any) {
      console.error("[AISupportConsole] defect link failed", e);
      setError(e?.response?.data?.detail || "Could not attach that to a defect.");
    }
  };

  /** Open a defect that no conversation is attached to yet -- from the
   *  register, with no session involved. Until now a defect could only be born
   *  by attaching a conversation to it, so a fault somebody already knew about
   *  could not be written down before it was reported. */
  const createDefect = async (title: string) => {
    const clean = title.trim();
    if (!clean) return;
    setError(null);
    try {
      await apiService.linkAISupportDefect(token(), { title: clean });
      setNewDefectTitle("");
      await loadDefects();
    } catch (e: any) {
      console.error("[AISupportConsole] defect create failed", e);
      setError(e?.response?.data?.detail || "Could not open that defect.");
    }
  };

  const renameDefect = async (defectId: number, title: string) => {
    const clean = title.trim();
    if (!clean) return;
    setError(null);
    try {
      await apiService.updateAISupportDefect(token(), defectId, { title: clean });
      setRenamingDefect(null);
      await loadDefects();
    } catch (e: any) {
      console.error("[AISupportConsole] defect rename failed", e);
      setError(e?.response?.data?.detail || "Could not rename that defect.");
    }
  };

  const classifySession = async (issueClass: string, causeCode?: string) => {
    if (!selectedId) return;
    setError(null);
    setIsClassifying(true);
    try {
      await apiService.classifyAISupportSession(token(), selectedId, issueClass, causeCode);
      await Promise.all([loadDetail(selectedId), loadSessions()]);
    } catch (e: any) {
      console.error("[AISupportConsole] classify failed", e);
      setError(e?.response?.data?.detail || "Could not record that classification.");
    } finally {
      setIsClassifying(false);
    }
  };

  const closeSession = async () => {
    if (!selectedId) return;
    setError(null);
    setIsClosing(true);
    try {
      await apiService.closeAISupportSession(token(), selectedId, closeNote.trim() || undefined);
      setCloseNote("");
      await Promise.all([loadDetail(selectedId), loadSessions()]);
    } catch (e: any) {
      console.error("[AISupportConsole] close failed", e);
      setError(e?.response?.data?.detail || "Could not close that conversation.");
    } finally {
      setIsClosing(false);
    }
  };

  const sendReply = async () => {
    const text = reply.trim();
    if (!text || !selectedId) return;
    setReply("");
    setError(null);
    try {
      await apiService.sendAISupportReply(token(), selectedId, text);
      await loadDetail(selectedId);
      await loadSessions();
    } catch (e: any) {
      console.error("[AISupportConsole] reply failed", e);
      setError(e?.response?.data?.detail || "Could not send that reply.");
    }
  };

  /**
   * Refresh both panes. Reloading only the session list looked like the button
   * did nothing: the list rarely changes visibly, and the conversation being
   * read is what the supporter actually wants brought up to date.
   */
  const refreshAll = useCallback(async () => {
    setIsRefreshing(true);
    setError(null);
    try {
      await Promise.all([
        loadSessions(),
        selectedIdRef.current ? loadDetail(selectedIdRef.current) : Promise.resolve(),
      ]);
    } finally {
      setTimeout(() => setIsRefreshing(false), 350);
    }
  }, [loadSessions, loadDetail]);

  const loadKb = useCallback(async () => {
    try {
      const res = await apiService.getAISupportKB(token());
      setKb(res.entries);
    } catch (e: any) {
      console.error("[AISupportConsole] KB load failed", e);
      setError(e?.response?.data?.detail || "Could not load the knowledge base.");
    }
  }, []);

  useEffect(() => { if (tab === "kb") void loadKb(); }, [tab, loadKb]);

  // The knowledge base is small, so it is fetched whole and filtered here --
  // which keeps the draft count on the tab honest whatever filter is applied.
  // The server returns exactly the chip's sessions, so there is nothing left to
  // narrow here.
  const visibleSessions = sessions;

  const visibleDefects = useMemo(
    () => (defectFilter ? defects.filter((d) => d.status === defectFilter) : defects),
    [defects, defectFilter]
  );

  const visibleKb = useMemo(() => {
    const byStatus = kbFilter ? kb.filter((e) => e.status === kbFilter) : kb;
    const q = kbSearch.trim().toLowerCase();
    if (!q) return byStatus;
    // Searching the whole entry -- an entry is usually remembered by a phrase
    // buried in its resolution, or by the id quoted in a conversation.
    return byStatus.filter((e) =>
      [
        String(e.id),
        e.title,
        e.symptom || "",
        e.resolution || "",
        (e.tags || []).join(" "),
        e.source_session_id ? String(e.source_session_id) : "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [kb, kbFilter, kbSearch]);

  const loadRevisions = useCallback(async () => {
    try {
      const res = await apiService.getAISupportKBRevisions(token());
      setRevisions(res.revisions || []);
      setRevisionEntries(res.entries || {});
    } catch {
      setRevisions([]);
    }
  }, []);

  useEffect(() => { void loadRevisions(); }, [loadRevisions]);

  const decideRevision = async (revisionId: number, accept: boolean) => {
    setSavingKb(true);
    setError(null);
    try {
      await apiService.decideAISupportKBRevision(token(), revisionId, accept);
      await Promise.all([loadRevisions(), loadKb()]);
    } catch (e: any) {
      console.error("[AISupportConsole] revision decide failed", e);
      setError(e?.response?.data?.detail || "Could not decide that revision.");
    } finally {
      setSavingKb(false);
    }
  };

  const deleteKb = async (entry: AISupportKBEntry) => {
    if (!window.confirm(
      `Delete “${entry.title}” permanently?\n\nArchiving can be undone; this cannot. ` +
      `Sessions that used it keep their record of having done so.`
    )) return;
    setSavingKb(true);
    setError(null);
    try {
      await apiService.deleteAISupportKBEntry(token(), entry.id);
      await loadKb();
    } catch (e: any) {
      console.error("[AISupportConsole] KB delete failed", e);
      setError(e?.response?.data?.detail || "Could not delete that entry.");
    } finally {
      setSavingKb(false);
    }
  };

  const saveAuthored = async (status: "draft" | "published") => {
    if (!authoring?.title?.trim()) {
      setError("An entry needs a title.");
      return;
    }
    setSavingKb(true);
    setError(null);
    try {
      // No id means create; the backend files it as a manual entry.
      await apiService.saveAISupportKBEntry({
        token: token(),
        title: authoring.title,
        symptom: authoring.symptom ?? undefined,
        resolution: authoring.resolution ?? undefined,
        kind: authoring.kind ?? undefined,
        area: authoring.area ?? undefined,
        status,
      });
      setAuthoring(null);
      await loadKb();
    } catch (e: any) {
      console.error("[AISupportConsole] KB create failed", e);
      setError(e?.response?.data?.detail || "Could not save that entry.");
    } finally {
      setSavingKb(false);
    }
  };

  const saveKb = async (entry: AISupportKBEntry, status?: AISupportKBEntry["status"]) => {
    setSavingKb(true);
    setError(null);
    try {
      await apiService.saveAISupportKBEntry({
        token: token(),
        id: entry.id,
        title: entry.title,
        symptom: entry.symptom ?? undefined,
        resolution: entry.resolution ?? undefined,
        kind: entry.kind ?? undefined,
        area: entry.area ?? undefined,
        status: status ?? entry.status,
      });
      setEditing(null);
      await loadKb();
    } catch (e: any) {
      console.error("[AISupportConsole] KB save failed", e);
      setError(e?.response?.data?.detail || "Could not save that entry.");
    } finally {
      setSavingKb(false);
    }
  };

  const selected = useMemo(
    () => sessions.find((s) => s.id === selectedId) || detail?.session || null,
    [sessions, selectedId, detail]
  );

  // Offer the defects already recorded against this cause, so a repeat becomes
  // a count on the existing fault rather than a second record for it.
  const selectedCause = selected?.cause_code || null;
  useEffect(() => {
    if (!selectedCause) { setDefectOptions([]); return; }
    apiService
      .getAISupportDefects(token(), { for_cause: selectedCause })
      .then((r) => setDefectOptions(r.defects || []))
      .catch(() => setDefectOptions([]));
  }, [selectedCause]);

  return (
    <Layout
      user={user}
      onLogout={onLogout}
      onNavigate={onNavigate}
      activeMenuItem="ai-support"
      taskCount={taskCount}
      whatsappUnreadCount={whatsappUnreadCount}
    >
      <div style={{ padding: "16px 20px" }}>
        <style>{PULSE_KEYFRAMES}</style>
        <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>AI Support</h2>
          {stats && (
            <span style={{ fontSize: 12.5, color: "#6b7b7a" }}>
              {stats.total_rated > 0
                ? `${Math.round((stats.resolution_rate ?? 0) * 100)}% resolved across ${stats.total_rated} rated conversations`
                : "No conversations rated yet"}
            </span>
          )}
          <div style={{ display: "flex", gap: 4 }}>
            {(["sessions", "defects", "rules", "kb"] as const).map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => setTab(name)}
                style={{
                  border: `1px solid ${tab === name ? TEAL : "#cfdcda"}`,
                  background: tab === name ? "#DCECEA" : "#fff",
                  color: tab === name ? TEAL : "#6b7b7a",
                  borderRadius: 4, padding: "4px 12px", fontSize: 12.5,
                  cursor: "pointer", fontWeight: tab === name ? 600 : 400,
                }}
              >
                {name === "sessions"
                  ? "Sessions"
                  : name === "rules"
                  ? "Rules"
                  : name === "defects"
                  ? `Defects${
                      defects.filter((d) => d.status === "open").length
                        ? ` (${defects.filter((d) => d.status === "open").length})`
                        : ""
                    }`
                  : `Knowledge base${
                      kb.filter((e) => e.status === "draft").length
                        ? ` (${kb.filter((e) => e.status === "draft").length})`
                        : ""
                    }`}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => void refreshAll()}
            disabled={isRefreshing}
            style={{
              marginInlineStart: 8, border: "1px solid #cfdcda",
              background: isRefreshing ? "#eef2f1" : "#fff",
              borderRadius: 4, padding: "4px 12px", fontSize: 12.5,
              cursor: isRefreshing ? "default" : "pointer",
              color: isRefreshing ? "#8b9a99" : "inherit",
              minWidth: 86,
            }}
          >
            {isRefreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {error && (
          <div style={{ border: `1px solid ${ROSE}`, background: "#F7E2E4", borderRadius: 5, padding: "8px 12px", fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        {tab === "sessions" && classStats && (
          <div style={{
            display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center",
            padding: "9px 13px", marginBottom: 12, borderRadius: 7,
            border: "1px solid #e4ebea", background: "#fbfcfc",
          }}>
            <span style={{
              fontFamily: "monospace", fontSize: 10, letterSpacing: "0.09em",
              textTransform: "uppercase", color: "#8b9a99",
            }}>
              What the load is
            </span>
            {["knowledge", "intervention", "defect"].map((c) => {
              const decidedCount = classStats.decided[c] || 0;
              const waitingCount = classStats.awaiting_decision[c] || 0;
              return (
                <span key={c} style={{ fontSize: 12.5, color: "#4a5a5c" }}>
                  <b style={{ color: CLASS_COLOR[c], fontSize: 15 }}>{decidedCount}</b>{" "}
                  {CLASS_LABEL[c].toLowerCase()}
                  {waitingCount > 0 && (
                    <span style={{ color: AMBER }}> +{waitingCount} proposed</span>
                  )}
                </span>
              );
            })}
            {Object.keys(classStats.causes).length > 0 && (
              <span style={{ fontSize: 12, color: "#6b7b7a", marginInlineStart: "auto" }}>
                most common:{" "}
                {Object.entries(classStats.causes)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 3)
                  .map(([code, n]) => `${CAUSE_LABEL(code)} (${n})`)
                  .join(" · ")}
              </span>
            )}
          </div>
        )}

        {tab === "rules" ? (
          <div>
            <div style={{ fontSize: 12.5, color: "#6b7b7a", marginBottom: 12, maxWidth: "74ch" }}>
              What the assistant reads out of the product itself. These are not stored
              and not approved — they are re-read from the source every time it answers,
              so what is below is exactly what it would use right now.
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
              {ruleAreas.map((a) => (
                <button
                  key={a}
                  type="button"
                  data-ai-id="console-rule-area"
                  onClick={() => setRuleArea(a)}
                  style={{
                    border: `1px solid ${ruleArea === a ? TEAL : "#dfe7e6"}`,
                    background: ruleArea === a ? "#DCECEA" : "#fff",
                    color: ruleArea === a ? TEAL : "#6b7b7a",
                    fontWeight: ruleArea === a ? 600 : 400,
                    borderRadius: 12, padding: "3px 12px", fontSize: 12, cursor: "pointer",
                  }}
                >
                  {a}
                </button>
              ))}
            </div>

            {rulesError && (
              <div style={{
                border: `1px solid ${ROSE}`, background: "#F7E2E4", borderRadius: 5,
                padding: "8px 12px", fontSize: 13, marginBottom: 12,
              }}>
                {rulesError}
              </div>
            )}

            {derived && (
              <>
                <div style={{
                  fontFamily: "monospace", fontSize: 11, color: "#8b9a99", marginBottom: 8,
                }}>
                  {derived.facts.length} rules · from {derived.source.file} · read{" "}
                  {formatClinicRelative(derived.generated_at, "en")}
                </div>
                <div style={{ display: "grid", gap: 7 }}>
                  {derived.facts.map((f) => (
                    <div key={f.key} style={{
                      border: "1px solid #e2e9e8", borderRadius: 8, padding: "11px 14px",
                      background: "#fff",
                    }}>
                      <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                        <span style={{ fontFamily: "monospace", fontSize: 12, color: "#3d4b4a" }}>
                          {f.key.replace(/_/g, " ")}
                        </span>
                        <b style={{ fontSize: 14, color: TEAL }}>
                          {typeof f.value === "object" && f.value !== null
                            ? JSON.stringify(f.value)
                            : String(f.value)}
                          {f.unit ? ` ${f.unit}` : ""}
                        </b>
                        <span style={{
                          marginInlineStart: "auto", fontFamily: "monospace",
                          fontSize: 10.5, color: "#9aa8a7", overflowWrap: "anywhere",
                        }}>
                          {f.source.file}
                          {f.source.line ? `:${f.source.line}` : ""}
                        </span>
                      </div>
                      {f.note && (
                        <div style={{ fontSize: 12.5, color: "#546462", marginTop: 5 }}>
                          {f.note}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : tab === "defects" ? (
          <div>
            <div style={{ fontSize: 12.5, color: "#6b7b7a", marginBottom: 10, maxWidth: "72ch" }}>
              Faults the assistant cannot resolve. Ordered by how often they have been
              run into — the count is what makes a case for fixing one, and everyone
              listed against a defect can be told when it ships.
            </div>

            {/* Opening one here, rather than only from a conversation, is what
                lets a fault be written down before anybody reports it. */}
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10 }}>
              <input
                data-ai-id="console-defect-new-title"
                value={newDefectTitle}
                dir="auto"
                onChange={(e) => setNewDefectTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void createDefect(newDefectTitle);
                }}
                placeholder="Title for a new defect — what a supporter would search for…"
                style={{
                  flex: 1, maxWidth: 460, border: "1px solid #cdd8d7", borderRadius: 5,
                  padding: "5px 9px", fontSize: 12.5, color: "#33403f",
                }}
              />
              <button
                type="button"
                data-ai-id="console-defect-create"
                disabled={!newDefectTitle.trim()}
                onClick={() => void createDefect(newDefectTitle)}
                style={{
                  border: `1px solid ${newDefectTitle.trim() ? TEAL : "#dfe7e6"}`,
                  background: newDefectTitle.trim() ? "#DCECEA" : "#fff",
                  color: newDefectTitle.trim() ? TEAL : "#9aa8a7",
                  borderRadius: 5, padding: "5px 12px", fontSize: 12,
                  cursor: newDefectTitle.trim() ? "pointer" : "default",
                }}
              >
                Add defect
              </button>
            </div>

            <FilterChips
              value={defectFilter}
              onChange={setDefectFilter}
              options={[
                { value: "", label: "All", count: defects.length },
                ...["open", "investigating", "fixed", "wont_fix"].map((st) => ({
                  value: st,
                  label: st === "wont_fix" ? "Won't fix" : st[0].toUpperCase() + st.slice(1),
                  count: defects.filter((d) => d.status === st).length,
                })),
              ]}
            />

            {defects.length === 0 ? (
              <div style={{ fontSize: 13, color: "#7a8a89" }}>
                No defects recorded yet. Add one above, or classify a conversation as a
                defect and attach it to one.
              </div>
            ) : visibleDefects.length === 0 ? (
              <div style={{ fontSize: 13, color: "#7a8a89" }}>
                None of the {defects.length} recorded defects are in that state.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {visibleDefects.map((d) => (
                  <div key={d.id} style={{
                    border: "1px solid #e2e9e8", borderRadius: 8, padding: "12px 14px",
                    background: "#fff", borderInlineStart: `3px solid ${
                      d.status === "fixed" ? TEAL : d.status === "wont_fix" ? "#9aa8a7" : ROSE
                    }`,
                  }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                      {renamingDefect === d.id ? (
                        <>
                          <input
                            data-ai-id="console-defect-title-edit"
                            autoFocus
                            dir="auto"
                            value={defectTitleDraft}
                            onChange={(e) => setDefectTitleDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") void renameDefect(d.id, defectTitleDraft);
                              if (e.key === "Escape") setRenamingDefect(null);
                            }}
                            style={{
                              fontSize: 14, fontWeight: 700, color: "#33403f", minWidth: 280,
                              border: `1px solid ${TEAL}`, borderRadius: 5, padding: "3px 7px",
                            }}
                          />
                          <button
                            type="button"
                            data-ai-id="console-defect-title-save"
                            onClick={() => void renameDefect(d.id, defectTitleDraft)}
                            style={{
                              border: `1px solid ${TEAL}`, background: "#DCECEA", color: TEAL,
                              borderRadius: 5, padding: "2px 9px", fontSize: 11.5, cursor: "pointer",
                            }}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setRenamingDefect(null)}
                            style={{
                              border: "1px solid #dfe7e6", background: "#fff", color: "#6b7b7a",
                              borderRadius: 5, padding: "2px 9px", fontSize: 11.5, cursor: "pointer",
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <b
                          data-ai-id="console-defect-title"
                          title="Click to rename"
                          onClick={() => {
                            setRenamingDefect(d.id);
                            setDefectTitleDraft(d.title || "");
                          }}
                          style={{ fontSize: 14, cursor: "text" }}
                        >
                          {d.title}
                        </b>
                      )}
                      <span style={{
                        fontFamily: "monospace", fontSize: 11, color: "#8b9a99",
                      }}>
                        #{d.id}
                        {d.cause_code ? ` · ${CAUSE_LABEL(d.cause_code)}` : ""}
                        {d.first_seen_at
                          ? ` · first seen ${formatClinicRelative(d.first_seen_at, "en")}`
                          : ""}
                      </span>
                      <span style={{
                        marginInlineStart: "auto", fontSize: 15, fontWeight: 700,
                        color: d.occurrence_count > 1 ? ROSE : "#6b7b7a",
                      }}>
                        {d.occurrence_count}×
                      </span>
                      <select
                        aria-label="Defect status"
                        data-ai-id="console-defect-status"
                        value={d.status}
                        onChange={async (e) => {
                          try {
                            await apiService.updateAISupportDefect(token(), d.id, {
                              status: e.target.value,
                            });
                            await loadDefects();
                          } catch {
                            setError("Could not change that defect's status.");
                          }
                        }}
                        style={{
                          border: "1px solid #cdd8d7", borderRadius: 5, padding: "2px 7px",
                          fontSize: 11.5, color: "#546462", background: "#fff",
                        }}
                      >
                        {["open", "investigating", "fixed", "wont_fix"].map((st) => (
                          <option key={st} value={st}>{st.replace("_", " ")}</option>
                        ))}
                      </select>
                    </div>
                    {d.symptom && (
                      <div style={{ fontSize: 12.5, color: "#546462", marginTop: 5 }}>
                        {d.symptom}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : tab === "kb" ? (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 12.5, color: "#6b7b7a", maxWidth: "64ch" }}>
                Entries distilled from conversations, plus anything written here
                directly — knowledge that nobody has had to ask for yet.
              </div>
              <button
                type="button"
                data-ai-id="console-kb-new"
                onClick={() =>
                  setAuthoring(authoring ? null : { title: "", symptom: "", resolution: "" })
                }
                style={{
                  marginInlineStart: "auto", border: `1px solid ${TEAL}`,
                  background: authoring ? "#DCECEA" : "#fff", color: TEAL,
                  borderRadius: 5, padding: "4px 12px", fontSize: 12.5,
                  cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap",
                }}
              >
                {authoring ? "Cancel" : "Write an entry"}
              </button>
            </div>

            {authoring && (
              <div style={{
                border: `1px solid ${TEAL}`, borderRadius: 8, padding: "14px 16px",
                background: "#fbfdfd", marginBottom: 14,
              }}>
                <div style={{
                  fontFamily: "monospace", fontSize: 10, letterSpacing: "0.09em",
                  textTransform: "uppercase", color: TEAL, marginBottom: 9,
                }}>
                  New entry · written by you
                </div>
                <input
                  type="text"
                  value={authoring.title || ""}
                  onChange={(e) => setAuthoring({ ...authoring, title: e.target.value })}
                  placeholder="Title — what goes wrong, specifically"
                  aria-label="Title"
                  data-ai-id="console-kb-new-title"
                  style={{
                    width: "100%", border: "1px solid #d5dedd", borderRadius: 5,
                    padding: "6px 10px", fontSize: 13.5, marginBottom: 8,
                  }}
                />
                <textarea
                  value={authoring.symptom || ""}
                  onChange={(e) => setAuthoring({ ...authoring, symptom: e.target.value })}
                  placeholder="Symptom — how a therapist would describe it, in English. Separate alternative phrasings with /"
                  aria-label="Symptom"
                  data-ai-id="console-kb-new-symptom"
                  rows={2}
                  style={{
                    width: "100%", border: "1px solid #d5dedd", borderRadius: 5,
                    padding: "6px 10px", fontSize: 13, marginBottom: 8, resize: "vertical",
                  }}
                />
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <select
                    value={authoring.kind || ""}
                    onChange={(e) => setAuthoring({
                      ...authoring, kind: (e.target.value || null) as AISupportKBEntry["kind"],
                    })}
                    aria-label="Kind"
                    data-ai-id="console-kb-new-kind"
                    style={{
                      flex: 1, border: "1px solid #d5dedd", borderRadius: 5,
                      padding: "6px 8px", fontSize: 12.5, background: "#fff",
                    }}
                  >
                    <option value="">Kind — decides how it ranks…</option>
                    <option value="symptom">symptom — a complaint, and the checks in order</option>
                    <option value="how-to">how-to — a task they want to carry out</option>
                    <option value="reference">reference — how a mechanism works</option>
                    <option value="policy">policy — whose question it is</option>
                  </select>
                  <select
                    value={authoring.area || ""}
                    onChange={(e) => setAuthoring({
                      ...authoring, area: (e.target.value || null) as AISupportKBEntry["area"],
                    })}
                    aria-label="Area"
                    data-ai-id="console-kb-new-area"
                    style={{
                      flex: 1, border: "1px solid #d5dedd", borderRadius: 5,
                      padding: "6px 8px", fontSize: 12.5, background: "#fff",
                    }}
                  >
                    <option value="">Area — the subject it belongs to…</option>
                    {AI_SUPPORT_KB_AREAS.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  value={authoring.resolution || ""}
                  onChange={(e) => setAuthoring({ ...authoring, resolution: e.target.value })}
                  placeholder="Resolution — read by the assistant, not the therapist. What to check, in order, what each finding means, and what to tell them. Keep Hebrew only for interface labels they must match on screen."
                  aria-label="Resolution"
                  data-ai-id="console-kb-new-resolution"
                  rows={8}
                  style={{
                    width: "100%", border: "1px solid #d5dedd", borderRadius: 5,
                    padding: "6px 10px", fontSize: 13, marginBottom: 9,
                    resize: "vertical", lineHeight: 1.5,
                  }}
                />
                <div style={{ display: "flex", gap: 7 }}>
                  <button
                    type="button"
                    disabled={savingKb}
                    onClick={() => void saveAuthored("published")}
                    style={{
                      border: "none", background: TEAL, color: "#fff", borderRadius: 5,
                      padding: "5px 14px", fontSize: 12.5, cursor: "pointer", fontWeight: 600,
                    }}
                  >
                    {savingKb ? "Saving…" : "Save & publish"}
                  </button>
                  <button
                    type="button"
                    disabled={savingKb}
                    onClick={() => void saveAuthored("draft")}
                    style={{
                      border: "1px solid #cfdcda", background: "#fff", color: "#546462",
                      borderRadius: 5, padding: "5px 14px", fontSize: 12.5, cursor: "pointer",
                    }}
                  >
                    Save as draft
                  </button>
                </div>
              </div>
            )}

            {revisions.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{
                  fontFamily: "monospace", fontSize: 10.5, letterSpacing: "0.09em",
                  textTransform: "uppercase", color: AMBER, marginBottom: 8,
                }}>
                  {revisions.length} proposed change{revisions.length > 1 ? "s" : ""} to existing entries
                </div>
                <div style={{ display: "grid", gap: 10 }}>
                  {revisions.map((rev) => {
                    const target = revisionEntries[String(rev.entry_id)];
                    return (
                      <div key={rev.id} style={{
                        border: `1px solid ${AMBER}`, borderRadius: 8,
                        padding: "13px 15px", background: "#FBF6EC",
                      }}>
                        <div style={{ fontSize: 13.5, marginBottom: 6 }}>
                          <b>{target?.title || `Entry #${rev.entry_id}`}</b>
                          <span style={{ color: "#8b7a55", fontSize: 12 }}>
                            {" "}· from conversation #{rev.source_session_id}
                          </span>
                        </div>
                        {rev.what_is_missing && (
                          <div style={{ fontSize: 13, color: "#4a4433", marginBottom: 9 }}>
                            <b>Missing:</b> {rev.what_is_missing}
                          </div>
                        )}
                        {/* Only the fields the proposal carries are shown, because
                            only those would change. */}
                        {(["symptom", "resolution"] as const).map((field) =>
                          rev[field] ? (
                            <div key={field} style={{ marginBottom: 9 }}>
                              <div style={{
                                fontFamily: "monospace", fontSize: 10,
                                letterSpacing: "0.08em", textTransform: "uppercase",
                                color: "#8b7a55", marginBottom: 3,
                              }}>
                                {field} — proposed
                              </div>
                              <div style={{
                                display: "grid", gap: 6,
                                gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
                              }}>
                                <div style={{
                                  background: "#fff", border: "1px solid #e6ded0",
                                  borderRadius: 5, padding: "7px 9px", fontSize: 12.5,
                                  color: "#7a7364", whiteSpace: "pre-wrap",
                                }}>
                                  {target?.[field] || <i>(empty)</i>}
                                </div>
                                <div style={{
                                  background: "#fff", border: `1px solid ${TEAL}`,
                                  borderRadius: 5, padding: "7px 9px", fontSize: 12.5,
                                  color: "#2f3d3c", whiteSpace: "pre-wrap",
                                }}>
                                  {rev[field]}
                                </div>
                              </div>
                            </div>
                          ) : null
                        )}
                        <div style={{ display: "flex", gap: 7 }}>
                          <button
                            type="button"
                            data-ai-id="console-revision-accept"
                            disabled={savingKb}
                            onClick={() => void decideRevision(rev.id, true)}
                            style={{
                              border: "none", background: TEAL, color: "#fff",
                              borderRadius: 5, padding: "4px 14px", fontSize: 12.5,
                              cursor: "pointer", fontWeight: 600,
                            }}
                          >
                            Accept
                          </button>
                          <button
                            type="button"
                            data-ai-id="console-revision-reject"
                            disabled={savingKb}
                            onClick={() => void decideRevision(rev.id, false)}
                            style={{
                              border: "1px solid #cfdcda", background: "#fff",
                              color: "#546462", borderRadius: 5, padding: "4px 14px",
                              fontSize: 12.5, cursor: "pointer",
                            }}
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <FilterChips
                value={kbFilter}
                onChange={setKbFilter}
                options={[
                  { value: "", label: "All", count: kb.length },
                  { value: "draft", label: "Drafts", count: kb.filter((e) => e.status === "draft").length },
                  { value: "published", label: "Published", count: kb.filter((e) => e.status === "published").length },
                  { value: "archived", label: "Archived", count: kb.filter((e) => e.status === "archived").length },
                ]}
              />
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
                <input
                  data-ai-id="console-kb-search"
                  value={kbSearch}
                  dir="auto"
                  onChange={(e) => setKbSearch(e.target.value)}
                  placeholder="Search id, title or text…"
                  style={{
                    border: "1px solid #dfe9e7", borderRadius: 5, padding: "6px 10px",
                    fontSize: 12.5, minWidth: 240,
                  }}
                />
                {kbSearch && (
                  <span style={{ fontSize: 11.5, color: "#8b9a99", whiteSpace: "nowrap" }}>
                    {visibleKb.length} match{visibleKb.length === 1 ? "" : "es"}
                    <button
                      type="button"
                      onClick={() => setKbSearch("")}
                      style={{
                        marginLeft: 8, border: "none", background: "none", color: TEAL,
                        fontSize: 11.5, cursor: "pointer", padding: 0,
                      }}
                    >
                      clear
                    </button>
                  </span>
                )}
              </div>
            </div>
            <div style={{ border: "1px solid #dfe7e6", borderRadius: 6, background: "#fff" }}>
            {visibleKb.length === 0 && (
              <div style={{ padding: 18, fontSize: 13, color: "#7a8a89" }}>
                {kbSearch.trim()
                  ? `No entry matches "${kbSearch.trim()}".`
                  : "Nothing here yet. Entries are written up automatically from conversations that were solved, or that you corrected, and wait here for review."}
              </div>
            )}
            {visibleKb.map((entry) => {
              const isDraft = entry.status === "draft";
              const isEditing = editing?.id === entry.id;
              return (
                <div
                  key={entry.id}
                  style={{
                    borderBottom: "1px solid #eef2f1", padding: "14px 18px",
                    background: isDraft ? "#FBF7EC" : "#fff",
                  }}
                >
                  <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                    <Pill
                      text={entry.status}
                      color={isDraft ? AMBER : entry.status === "published" ? TEAL : "#6b7b7a"}
                      bg={isDraft ? "#F6EBD6" : entry.status === "published" ? "#DCECEA" : "#eef1f1"}
                    />
                    <span
                      title="Knowledge base entry id"
                      style={{ fontSize: 11.5, fontFamily: "monospace", color: "#8b9a99" }}
                    >
                      #{entry.id}
                    </span>
                    <b style={{ fontSize: 14 }}>{entry.title}</b>
                    <span
                      title={entry.kind && entry.area
                        ? "Kind and area decide how this entry ranks"
                        : "No kind or area set — ranking has nothing to work with"}
                      style={{
                        fontSize: 11, fontFamily: "monospace", flex: 1,
                        color: entry.kind && entry.area ? "#6b7b7a" : AMBER,
                      }}
                    >
                      {entry.kind || "no kind"} · {entry.area || "no area"}
                      {entry.related_ids && entry.related_ids.length
                        ? ` · also ${entry.related_ids.map((i) => `#${i}`).join(" ")}`
                        : ""}
                    </span>
                    <span style={{ fontSize: 11, color: "#8b9a99", fontFamily: "monospace" }}>
                      {entry.source_type.replace("_", " ")}
                      {entry.source_session_id ? ` · session #${entry.source_session_id}` : ""}
                      {entry.status === "published"
                        ? ` · used ${entry.use_count} · helped ${entry.helpful_count}`
                        : ""}
                    </span>
                  </div>

                  {isEditing ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                      <input
                        value={editing.title}
                        onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                        style={{ border: "1px solid #dfe9e7", borderRadius: 5, padding: "6px 10px", fontSize: 13 }}
                      />
                      <div style={{ display: "flex", gap: 8 }}>
                        <select
                          value={editing.kind || ""}
                          onChange={(e) => setEditing({
                            ...editing, kind: (e.target.value || null) as AISupportKBEntry["kind"],
                          })}
                          aria-label="Kind"
                          style={{
                            flex: 1, border: "1px solid #dfe9e7", borderRadius: 5,
                            padding: "6px 8px", fontSize: 12.5, background: "#fff",
                          }}
                        >
                          <option value="">no kind</option>
                          <option value="symptom">symptom</option>
                          <option value="how-to">how-to</option>
                          <option value="reference">reference</option>
                          <option value="policy">policy</option>
                        </select>
                        <select
                          value={editing.area || ""}
                          onChange={(e) => setEditing({
                            ...editing, area: (e.target.value || null) as AISupportKBEntry["area"],
                          })}
                          aria-label="Area"
                          style={{
                            flex: 1, border: "1px solid #dfe9e7", borderRadius: 5,
                            padding: "6px 8px", fontSize: 12.5, background: "#fff",
                          }}
                        >
                          <option value="">no area</option>
                          {AI_SUPPORT_KB_AREAS.map((a) => (
                            <option key={a} value={a}>{a}</option>
                          ))}
                        </select>
                      </div>
                      <textarea
                        value={editing.symptom || ""}
                        onChange={(e) => setEditing({ ...editing, symptom: e.target.value })}
                        rows={2}
                        dir="auto"
                        placeholder="How a therapist describes it"
                        style={{ border: "1px solid #dfe9e7", borderRadius: 5, padding: "6px 10px", fontSize: 12.5, fontFamily: "inherit" }}
                      />
                      <textarea
                        value={editing.resolution || ""}
                        onChange={(e) => setEditing({ ...editing, resolution: e.target.value })}
                        rows={6}
                        dir="auto"
                        placeholder="What to check, in order, and what each finding means"
                        style={{ border: "1px solid #dfe9e7", borderRadius: 5, padding: "6px 10px", fontSize: 12.5, fontFamily: "inherit" }}
                      />
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          type="button"
                          disabled={savingKb}
                          onClick={() => void saveKb(editing, "published")}
                          style={{ padding: "5px 16px", borderRadius: 4, border: "none", background: TEAL, color: "#fff", fontSize: 12.5, cursor: "pointer" }}
                        >
                          Save &amp; publish
                        </button>
                        <button
                          type="button"
                          disabled={savingKb}
                          onClick={() => void saveKb(editing)}
                          style={{ padding: "5px 16px", borderRadius: 4, border: "1px solid #b9c6c4", background: "#fff", fontSize: 12.5, cursor: "pointer" }}
                        >
                          Save as draft
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditing(null)}
                          style={{ padding: "5px 16px", borderRadius: 4, border: "1px solid #b9c6c4", background: "#fff", fontSize: 12.5, cursor: "pointer" }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div dir="auto" style={{ fontSize: 12.5, color: "#6b7b7a", marginTop: 6 }}>
                        {entry.symptom}
                      </div>
                      <div dir="auto" style={{
                        fontSize: 12.5, marginTop: 8, whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere", color: "#3c4a4c",
                      }}>
                        {(entry.resolution || "").slice(0, 400)}
                        {(entry.resolution || "").length > 400 ? "…" : ""}
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                        <button
                          type="button"
                          onClick={() => setEditing(entry)}
                          style={{ padding: "4px 14px", borderRadius: 4, border: `1px solid ${TEAL}`, background: "#fff", color: TEAL, fontSize: 12, cursor: "pointer" }}
                        >
                          {isDraft ? "Review" : "Edit"}
                        </button>
                        {isDraft && (
                          <button
                            type="button"
                            disabled={savingKb}
                            onClick={() => void saveKb(entry, "published")}
                            style={{ padding: "4px 14px", borderRadius: 4, border: "none", background: TEAL, color: "#fff", fontSize: 12, cursor: "pointer" }}
                          >
                            Publish
                          </button>
                        )}
                        {entry.status !== "archived" && (
                          <button
                            type="button"
                            disabled={savingKb}
                            onClick={() => void saveKb(entry, "archived")}
                            style={{ padding: "4px 14px", borderRadius: 4, border: "1px solid #b9c6c4", background: "#fff", color: "#6b7b7a", fontSize: 12, cursor: "pointer" }}
                          >
                            Archive
                          </button>
                        )}
                        {entry.status === "archived" && (
                          // Only archived entries, and only behind a confirm:
                          // archiving is reversible and this is not.
                          <button
                            type="button"
                            data-ai-id="console-kb-delete"
                            disabled={savingKb}
                            onClick={() => void deleteKb(entry)}
                            title="Delete permanently. Archiving is reversible; this is not."
                            style={{ padding: "4px 14px", borderRadius: 4, border: `1px solid ${ROSE}`, background: "#fff", color: ROSE, fontSize: 12, cursor: "pointer" }}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
            </div>
          </div>
        ) : (
        <div style={{
          display: "grid", gridTemplateColumns: "280px minmax(0, 1fr)",
          gap: 16, alignItems: "start",
        }}>
          {/* --- session list ------------------------------------------- */}
          <div>
            <label style={{
              display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12,
              color: "#546462", cursor: "pointer", whiteSpace: "nowrap",
              marginBottom: 8,
            }}>
              <input
                type="checkbox"
                data-ai-id="console-include-test"
                checked={includeTest}
                onChange={(e) => setIncludeTest(e.target.checked)}
              />
              Include test sessions
              <span style={{ color: "#9aa8a7" }}>
                (opened by support admins)
              </span>
            </label>

            <FilterChips
              value={sessionFilter}
              onChange={setSessionFilter}
              options={[
                // Broad first, then the narrower cuts of it. "Open" is
                // everything still going on -- a session awaiting approval,
                // escalated or mid-answer is open too, not only an idle one.
                // Every count comes from the server: a filter with no number
                // beside it would read as "unknown", not as "none".
                { value: "", label: "All" },
                { value: "open", label: "Open" },
                { value: "awaiting_approval", label: "Needs approval" },
                { value: "escalated", label: "Escalated" },
                { value: "live", label: "Live" },
                { value: "needs_class", label: "Needs classifying" },
                { value: "working", label: "Working" },
                { value: "closed", label: "Closed" },
              ].map((o) => ({
                // No badge at all until the counts have actually arrived. A
                // zero here would claim "none" when the truth is "not known
                // yet" — and against a server that does not send them, every
                // chip read 0 while the list beside it was full.
                ...o,
                count: hasFilterCounts ? filterCounts[o.value] ?? 0 : undefined,
              }))}
            />
            <div style={{ border: "1px solid #dfe7e6", borderRadius: 6, overflow: "hidden", background: "#fff" }}>
            {visibleSessions.length === 0 && (
              <div style={{ padding: 16, fontSize: 13, color: "#7a8a89" }}>No conversations yet.</div>
            )}
            {visibleSessions.map((s) => {
              const style = STATUS_STYLE[s.status] || STATUS_STYLE.closed;
              const isSelected = s.id === selectedId;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSelectedId(s.id)}
                  style={{
                    display: "block", width: "100%", textAlign: "left", border: "none",
                    borderBottom: "1px solid #eef2f1", cursor: "pointer",
                    padding: "10px 12px",
                    background: isSelected ? "#f2f8f7" : "#fff",
                    boxShadow: isSelected ? `inset 3px 0 0 ${TEAL}` : "none",
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{s.user_name || `User ${s.user_id}`}</div>
                  <div style={{ fontSize: 10.5, color: "#8b9a99", margin: "2px 0 5px" }}>
                    {formatClinicRelative(s.last_activity_at || s.updated_at, "en")} · #{s.id}
                    {s.clinic_id != null ? ` · clinic ${s.clinic_id}` : ""}
                    {s.had_guidance ? " · guided" : ""}
                  </div>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {s.is_live && (
                      <Pill text={LIVE_STYLE.label} color={LIVE_STYLE.color} bg={LIVE_STYLE.bg} />
                    )}
                    <Pill
                      text={s.is_stalled ? "Stalled" : style.label}
                      color={s.is_stalled ? ROSE : style.color}
                      bg={s.is_stalled ? "#F7E2E4" : style.bg}
                    />
                    {s.is_test && (
                      <Pill text="Test" color="#8b7fa8" bg="#f2effa" />
                    )}
                    {(s.issue_class || s.proposed_class) && (
                      <Pill
                        text={
                          (CLASS_LABEL[s.issue_class || s.proposed_class || ""] ||
                            s.issue_class || s.proposed_class || "") +
                          (s.issue_class ? "" : "?")
                        }
                        title={
                          s.issue_class
                            ? s.cause_code
                              ? CAUSE_LABEL(s.cause_code)
                              : undefined
                            : "Proposed by the assistant — not yet decided"
                        }
                        color={CLASS_COLOR[s.issue_class || s.proposed_class || ""] || "#6b7b7a"}
                        bg="#fff"
                      />
                    )}
                    {s.outcome && (
                      <Pill
                        text={OUTCOME_LABEL[s.outcome] || s.outcome}
                        title={s.outcome_note || undefined}
                        color="#6b7b7a"
                        bg="#eef1f1"
                      />
                    )}
                  </div>
                  {s.title && (
                    <div style={{ fontSize: 11.5, color: "#6b7b7a", marginTop: 5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.title}
                    </div>
                  )}
                </button>
              );
            })}
            </div>
          </div>

          {/* --- transcript + controls ---------------------------------- */}
          <div style={{
            border: "1px solid #dfe7e6", borderRadius: 6, background: "#fff",
            minHeight: 420, display: "flex", flexDirection: "column",
            minWidth: 0, overflow: "hidden",
          }}>
            {!selected && (
              <div style={{ padding: 20, fontSize: 13, color: "#7a8a89" }}>
                Select a conversation to watch it.
              </div>
            )}

            {selected && (
              <>
                <div style={{ padding: "11px 16px", borderBottom: "1px solid #eef2f1", background: "#f7faf9", display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
                  <b style={{ fontSize: 14 }}>{selected.user_name || `User ${selected.user_id}`}</b>
                  <span style={{
                    fontFamily: "monospace", fontSize: 11, color: "#8b9a99",
                    overflowWrap: "anywhere",
                  }}>
                    {formatClinicTime(selected.last_activity_at || selected.updated_at)} (Israel) · #{selected.id} · {selected.lang}
                    {selected.input_tokens || selected.output_tokens
                      ? ` · ${selected.input_tokens + selected.output_tokens} tokens`
                      : ""}
                  </span>
                  <label style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    fontSize: 11.5, color: "#546462", cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}>
                    <input
                      type="checkbox"
                      data-ai-id="console-conversation-only"
                      checked={conversationOnly}
                      onChange={(e) => setConversationOnly(e.target.checked)}
                    />
                    Conversation only
                  </label>
                  {/* Shown whatever the session's state: written feedback can
                      arrive after the verdict, and on an escalated conversation
                      it is the thing a supporter most needs to read. */}
                  {selected.outcome_note && (
                    <span style={{ fontSize: 11.5, color: "#6b7b7a", fontStyle: "italic" }}>
                      “{selected.outcome_note}”
                    </span>
                  )}
                  {selected.status !== "closed" && (
                    // Therapists often get their answer and simply walk away,
                    // leaving the conversation open. Support needs to file
                    // those away, or the list that matters gets buried. The
                    // reason is optional but worth asking for: months later,
                    // "closed by support" with no note explains nothing.
                    <div style={{ marginInlineStart: "auto", display: "flex", gap: 6, alignItems: "center" }}>
                      <input
                        type="text"
                        value={closeNote}
                        onChange={(e) => setCloseNote(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") void closeSession(); }}
                        placeholder="Why close it? (optional)"
                        aria-label="Reason for closing this conversation"
                        data-ai-id="console-close-note"
                        disabled={isClosing}
                        style={{
                          width: 190, border: "1px solid #d5dedd", borderRadius: 5,
                          padding: "3px 8px", fontSize: 11.5, color: "#3d4b4a",
                        }}
                      />
                      <button
                        type="button"
                        data-ai-id="console-close-session"
                        onClick={() => void closeSession()}
                        disabled={isClosing}
                        title="Close this conversation without recording a verdict on the assistant"
                        style={{
                          border: "1px solid #c3cfce", background: "#fff", borderRadius: 5,
                          padding: "3px 10px", fontSize: 11.5, cursor: "pointer",
                          color: "#546462", whiteSpace: "nowrap",
                        }}
                      >
                        {isClosing ? "Closing…" : "Close conversation"}
                      </button>
                    </div>
                  )}
                </div>

                {(() => {
                  const decided = selected.issue_class;
                  const proposed = selected.proposed_class;
                  // Only defect and intervention wait on a person: the assistant
                  // settles a question it answered, and the same symptom is a
                  // fault in one clinic and a stuck record in another.
                  const waiting = !decided && (proposed === "defect" || proposed === "intervention");
                  if (!decided && !proposed) return null;
                  return (
                    <div style={{
                      margin: "12px 16px 0", padding: "11px 14px", borderRadius: 6,
                      border: `1px solid ${waiting ? AMBER : "#e2e9e8"}`,
                      background: waiting ? "#FBF5E9" : "#fbfcfc",
                    }}>
                      <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{
                          fontFamily: "monospace", fontSize: 10, letterSpacing: "0.09em",
                          textTransform: "uppercase", color: waiting ? AMBER : "#6b7b7a",
                        }}>
                          {waiting ? "Needs your decision" : "Classified"}
                        </span>
                        {decided ? (
                          <Pill
                            text={CLASS_LABEL[decided] || decided}
                            color={CLASS_COLOR[decided] || "#6b7b7a"}
                            bg="#fff"
                          />
                        ) : (
                          <span style={{ fontSize: 12.5, color: "#4a5a5c" }}>
                            The assistant proposed{" "}
                            <b>{CLASS_LABEL[proposed || ""] || proposed}</b>
                          </span>
                        )}
                        {selected.cause_code && (
                          <span style={{ fontSize: 12, color: "#6b7b7a" }}>
                            · {CAUSE_LABEL(selected.cause_code)}
                            {selected.cause_by_assistant ? " (assistant)" : ""}
                          </span>
                        )}
                      </div>
                      {selected.cause_note && (
                        <div style={{ fontSize: 12.5, color: "#546462", marginTop: 5, fontStyle: "italic" }}>
                          “{selected.cause_note}”
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 6, marginTop: 9, flexWrap: "wrap", alignItems: "center" }}>
                        {["knowledge", "intervention", "defect"].map((c) => (
                          <button
                            key={c}
                            type="button"
                            data-ai-id="console-classify"
                            disabled={isClassifying}
                            onClick={() => void classifySession(c, selected.cause_code || undefined)}
                            title={
                              c === "defect"
                                ? "The system misbehaved — goes to engineering, never closes as solved"
                                : c === "intervention"
                                ? "Correct by design, but this record is stuck — fixed by an action"
                                : "The system worked; the user did not know something"
                            }
                            style={{
                              border: `1px solid ${decided === c ? CLASS_COLOR[c] : "#cdd8d7"}`,
                              background: decided === c ? "#fff" : "transparent",
                              color: decided === c ? CLASS_COLOR[c] : "#546462",
                              fontWeight: decided === c ? 600 : 400,
                              borderRadius: 5, padding: "3px 10px", fontSize: 11.5, cursor: "pointer",
                            }}
                          >
                            {CLASS_LABEL[c]}
                          </button>
                        ))}
                        {/* The assistant writes a cause because it did the
                            diagnosis; this is where a supporter corrects it. The
                            list is scoped to the class, since a defect cause and
                            a knowledge cause are different vocabularies. */}
                        <select
                          aria-label="Cause"
                          data-ai-id="console-cause"
                          disabled={isClassifying}
                          value={selected.cause_code || ""}
                          onChange={(e) =>
                            void classifySession(decided || proposed || "unknown", e.target.value || undefined)
                          }
                          style={{
                            border: "1px solid #cdd8d7", borderRadius: 5, padding: "3px 8px",
                            fontSize: 11.5, color: "#546462", background: "#fff",
                            marginInlineStart: 4, maxWidth: 230,
                          }}
                        >
                          <option value="">cause…</option>
                          {(causeCodes[decided || proposed || ""] || []).map((code) => (
                            <option key={code} value={code}>{CAUSE_LABEL(code)}</option>
                          ))}
                        </select>
                      </div>
                      {decided === "defect" && (
                        <div style={{ marginTop: 10, paddingTop: 9, borderTop: "1px solid #eef2f1" }}>
                          <div style={{
                            fontFamily: "monospace", fontSize: 10, letterSpacing: "0.09em",
                            textTransform: "uppercase", color: "#8b9a99", marginBottom: 6,
                          }}>
                            Known fault
                          </div>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                            <select
                              aria-label="Attach to a known defect"
                              data-ai-id="console-defect-pick"
                              value={selected.defect_id ?? ""}
                              onChange={(e) =>
                                void attachToDefect(
                                  e.target.value ? Number(e.target.value) : null,
                                  { symptom: selected.title || undefined,
                                    causeCode: selected.cause_code || undefined }
                                )
                              }
                              style={{
                                border: "1px solid #cdd8d7", borderRadius: 5, padding: "3px 8px",
                                fontSize: 11.5, color: "#546462", background: "#fff", maxWidth: 340,
                              }}
                            >
                              <option value="">not attached</option>
                              {/* Same cause first; then anything already open, so
                                  a mis-coded report can still be attached. */}
                              {[...defectOptions, ...defects.filter(
                                (d) => !defectOptions.some((o) => o.id === d.id)
                                  && (d.status === "open" || d.status === "investigating")
                              )].map((d) => (
                                <option key={d.id} value={d.id}>
                                  #{d.id} {d.title} ({d.occurrence_count})
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              data-ai-id="console-defect-new"
                              onClick={() =>
                                setNewFaultTitle(
                                  newFaultTitle === null ? selected.title || "" : null
                                )
                              }
                              style={{
                                border: "1px solid #cdd8d7", background: "#fff", borderRadius: 5,
                                padding: "3px 10px", fontSize: 11.5, cursor: "pointer", color: "#546462",
                              }}
                            >
                              New fault…
                            </button>
                            {selected.defect_id && (
                              <span style={{ fontSize: 11.5, color: ROSE }}>
                                reported{" "}
                                {defects.find((d) => d.id === selected.defect_id)?.occurrence_count ?? 1}×
                              </span>
                            )}
                          </div>
                          {newFaultTitle !== null && (
                            <div style={{ display: "flex", gap: 6, marginTop: 7, alignItems: "center" }}>
                              <input
                                data-ai-id="console-defect-new-fault-title"
                                autoFocus
                                dir="auto"
                                value={newFaultTitle}
                                onChange={(e) => setNewFaultTitle(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Escape") setNewFaultTitle(null);
                                  if (e.key === "Enter" && newFaultTitle.trim()) {
                                    void attachToDefect(null, {
                                      title: newFaultTitle.trim(),
                                      symptom: selected.title || undefined,
                                      causeCode: selected.cause_code || undefined,
                                    });
                                    setNewFaultTitle(null);
                                  }
                                }}
                                placeholder="Short title — what a supporter would search for next time"
                                style={{
                                  flex: 1, maxWidth: 340, border: `1px solid ${TEAL}`,
                                  borderRadius: 5, padding: "3px 8px", fontSize: 11.5,
                                  color: "#33403f",
                                }}
                              />
                              <button
                                type="button"
                                data-ai-id="console-defect-new-fault-save"
                                disabled={!newFaultTitle.trim()}
                                onClick={() => {
                                  void attachToDefect(null, {
                                    title: newFaultTitle.trim(),
                                    symptom: selected.title || undefined,
                                    causeCode: selected.cause_code || undefined,
                                  });
                                  setNewFaultTitle(null);
                                }}
                                style={{
                                  border: `1px solid ${newFaultTitle.trim() ? TEAL : "#dfe7e6"}`,
                                  background: newFaultTitle.trim() ? "#DCECEA" : "#fff",
                                  color: newFaultTitle.trim() ? TEAL : "#9aa8a7",
                                  borderRadius: 5, padding: "3px 10px", fontSize: 11.5,
                                  cursor: newFaultTitle.trim() ? "pointer" : "default",
                                }}
                              >
                                Open it
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {(detail?.pending_actions || []).map((action) => (
                  <div key={action.id} style={{ margin: "12px 16px 0", border: `1px solid ${AMBER}`, background: "#F6EBD6", borderRadius: 6, padding: "12px 14px" }}>
                    <div style={{ fontFamily: "monospace", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: AMBER, marginBottom: 6 }}>
                      Approval requested
                    </div>
                    <b style={{ fontSize: 14 }}>{action.action_name}</b>
                    {/* The exact payload, never a summary of it. */}
                    <pre style={{ fontFamily: "monospace", fontSize: 11.5, background: "#fff", border: "1px solid #e6ded0", borderRadius: 4, padding: "8px 10px", margin: "8px 0 10px", overflowX: "auto", maxWidth: "100%" }}>
                      {JSON.stringify(action.arguments ?? {}, null, 2)}
                    </pre>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        disabled={busyAction === action.id}
                        onClick={() => void decide(action, "approve")}
                        style={{ padding: "5px 16px", borderRadius: 4, border: `1px solid ${TEAL}`, background: TEAL, color: "#fff", fontSize: 12.5, cursor: "pointer" }}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        disabled={busyAction === action.id}
                        onClick={() => void decide(action, "deny")}
                        style={{ padding: "5px 16px", borderRadius: 4, border: "1px solid #b9c6c4", background: "#fff", color: "#4a5a5c", fontSize: 12.5, cursor: "pointer" }}
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                ))}

                <div
                  ref={transcriptRef}
                  onScroll={(e) => {
                    const el = e.currentTarget;
                    pinnedToBottomRef.current =
                      el.scrollHeight - el.scrollTop - el.clientHeight < 40;
                  }}
                  style={{ flex: 1, overflowY: "auto", padding: 16, maxHeight: 480 }}
                >
                  {(detail?.messages || [])
                    .filter((m) => !conversationOnly || isConversation(m))
                    .map((m) => (
                    <div key={m.id} style={{ marginBottom: 12 }}>
                      <div style={{ fontFamily: "monospace", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: m.role === "system" ? AMBER : "#8b9a99", marginBottom: 3 }}>
                        {m.role === "system" || m.role === "guidance"
                          ? "guidance · not shown to therapist"
                          : m.role === "support_reply"
                          ? "human support → therapist"
                          : m.role}
                        {m.visible_to === "support_only" ? " · support only" : ""}
                      </div>
                      <div
                        dir="auto"
                        style={{
                          whiteSpace: "pre-wrap",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                          fontSize: 13, lineHeight: 1.55,
                          background:
                            m.role === "user" ? "#f2f8f7"
                              : m.role === "system" || m.role === "guidance" ? "#FBF3E3"
                              : m.role === "support_reply" ? "#FDF6E3"
                              : "#fff",
                          border: "1px solid #eef2f1", borderRadius: 6, padding: "8px 11px",
                        }}
                      >
                        {renderBlocks(m, conversationOnly)}
                      </div>
                    </div>
                  ))}
                  {conversationOnly
                    && (detail?.messages || []).length > 0
                    && !(detail?.messages || []).some(isConversation) && (
                    <div style={{ fontSize: 12.5, color: "#7a8a89" }}>
                      Nothing was said in this conversation — it is all reasoning and
                      tool calls. Untick to see them.
                    </div>
                  )}
                  {(detail?.messages || []).length === 0 && !isProcessing && (
                    <div style={{ fontSize: 13, color: "#7a8a89" }}>Nothing said yet.</div>
                  )}

                  {isProcessing && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: TEAL, padding: "6px 2px" }}>
                      <span
                        style={{
                          width: 9, height: 9, borderRadius: "50%", background: TEAL,
                          animation: "aiSupportPulse 1.1s ease-in-out infinite",
                        }}
                      />
                      {activity ? `Assistant is checking ${activity}…` : "Assistant is working…"}
                    </div>
                  )}
                </div>

                <div style={{ borderTop: "1px solid #eef2f1", padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                  {/* Two different acts, so they are labelled rather than left
                      to be inferred: guidance steers the assistant and the
                      therapist never sees it; a reply goes straight to them. */}
                  <div>
                    <label style={{ display: "block", fontSize: 11, color: "#6b7b7a", marginBottom: 4 }}>
                      Reply to the therapist — they see this, from you
                    </label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") void sendReply(); }}
                        placeholder="Answer them directly…"
                        style={{ flex: 1, border: `1px solid ${AMBER}`, borderRadius: 5, padding: "7px 11px", fontSize: 13, outline: "none" }}
                      />
                      <button
                        type="button"
                        onClick={() => void sendReply()}
                        disabled={!reply.trim()}
                        style={{
                          padding: "6px 16px", borderRadius: 5, border: "none", fontSize: 12.5,
                          background: reply.trim() ? AMBER : "#d8cbb4", color: "#fff",
                          cursor: reply.trim() ? "pointer" : "default",
                        }}
                      >
                        Reply
                      </button>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: 11, color: "#6b7b7a", marginBottom: 4 }}>
                      Guidance for the assistant — the therapist does not see this.
                      Naming an action here runs it: your instruction is the approval.
                    </label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input
                        value={guidance}
                        onChange={(e) => setGuidance(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") void sendGuidance(); }}
                        placeholder="Tell the assistant what to do next…"
                        style={{ flex: 1, border: "1px solid #dfe9e7", borderRadius: 5, padding: "7px 11px", fontSize: 13, outline: "none" }}
                      />
                      <button
                        type="button"
                        onClick={() => void sendGuidance()}
                        disabled={!guidance.trim()}
                        style={{
                          padding: "6px 16px", borderRadius: 5, border: "none", fontSize: 12.5,
                          background: guidance.trim() ? TEAL : "#c4d4d2", color: "#fff",
                          cursor: guidance.trim() ? "pointer" : "default",
                        }}
                      >
                        Send
                      </button>
                    </div>
                    {actions.length > 0 && (
                      <div style={{
                        fontSize: 10.5, color: "#8b9a99", marginTop: 5,
                        fontFamily: "monospace", overflowWrap: "anywhere",
                      }}>
                        runnable: {actions.map((a) => a.name).join(" · ")}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        )}
      </div>
    </Layout>
  );
};

export default AISupportConsoleScreen;
